import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, Container } from "reactstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";

import AppBreadcrumb from "../../../Components/Common/AppBreadcrumb";
import DeleteModal from "../../../Components/Common/DeleteModal";
import ExportCSVModal from "../../../Components/Common/ExportCSVModal";
import { useProfile } from "../../../Components/Hooks/UserHooks";
import { useCurrencyFormatter } from "../../../Components/Hooks/useCurrencyFormatter";
import {
  deleteContract as onDeleteContract,
  getContracts as onGetContracts,
  getContractStats as onGetContractStats,
  getPhases as onGetPhases,
} from "../../../slices/thunks";
import { selectPhases } from "../../../slices/phases/selectors";
import { selectContractStats } from "../../../slices/contractStats/selectors";

import ContractWizardV2 from "../ContractWizardV2";
import ContractStatTiles from "./components/ContractStatTiles";
import ContractFilterBar from "./components/ContractFilterBar";
import ContractsTable from "./components/ContractsTable";
import ContractPaginationFooter from "./components/ContractPaginationFooter";
import {
  readParamsFromSearch,
  buildQueryString,
  paramsEqual,
  DEFAULT_ORDERING,
} from "./utils/filterParams";

const SEARCH_DEBOUNCE_MS = 500;

const selectContractProperties = createSelector(
  (state) => state.Contract,
  (contract) => ({
    contracts: contract.contracts,
    count: contract.count,
    totalPages: contract.totalPages,
    currentPage: contract.currentPage,
    pageSize: contract.pageSize,
    nextPage: contract.nextPage,
    previousPage: contract.previousPage,
    isContractSuccess: contract.isContractSuccess,
    loading: contract.loading,
  })
);

const ContractsList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAdmin, isEditor } = useProfile();
  const { formatCurrency } = useCurrencyFormatter();
  const canDelete = isAdmin || isEditor;

  const {
    contracts,
    count,
    totalPages,
    currentPage,
    pageSize,
    nextPage,
    previousPage,
    isContractSuccess,
    loading,
  } = useSelector(selectContractProperties);
  const phases = useSelector(selectPhases);
  const stats = useSelector(selectContractStats);

  const [searchParams, setSearchParams] = useSearchParams();
  const initialPage = Math.max(1, parseInt(searchParams.get("page") || "1", 10));
  const initialFilterParams = useMemo(() => readParamsFromSearch(searchParams), []);

  const [filterParams, setFilterParams] = useState(initialFilterParams);
  const [page, setPage] = useState(initialPage);
  const [searchInput, setSearchInput] = useState(initialFilterParams.search || "");

  const [contractModal, setContractModal] = useState(false);
  const [isExportCSV, setIsExportCSV] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const contractRef = useRef(null);

  // One-way URL sync: filterParams/page are the source of truth; the URL is
  // only ever rewritten to match them (never read back reactively), avoiding
  // the classic useSearchParams infinite-loop bug.
  useEffect(() => {
    const next = buildQueryString(filterParams, page);
    const currentStr = searchParams.toString();
    const nextStr = next.toString();
    if (currentStr !== nextStr) {
      setSearchParams(next, { replace: true });
    }
  }, [filterParams, page, searchParams, setSearchParams]);

  // Debounce the search box into filterParams.search (500ms, no minimum
  // length — unlike Unit List's 1000ms/3-char minimum, PVC numbers can be
  // just 1-2 digits).
  useEffect(() => {
    const handle = setTimeout(() => {
      setFilterParams((prev) => {
        const trimmed = searchInput.trim();
        if ((prev.search || "") === trimmed) return prev;
        const next = { ...prev };
        if (trimmed) next.search = trimmed;
        else delete next.search;
        return next;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Reset to page 1 whenever filters actually change (skip the initial mount).
  const skipFirstFilterChange = useRef(true);
  useEffect(() => {
    if (skipFirstFilterChange.current) {
      skipFirstFilterChange.current = false;
      return;
    }
    setPage(1);
  }, [filterParams]);

  useEffect(() => {
    dispatch(onGetPhases()).catch((err) => console.error("Failed to fetch phases:", err));
  }, [dispatch]);

  // List fetch — includes payment_status/ordering/page, unlike stats below.
  useEffect(() => {
    dispatch(onGetContracts({ ...filterParams, page, page_size: 20 })).catch((err) =>
      console.error("Failed to fetch contracts:", err)
    );
  }, [dispatch, page, filterParams]);

  // Stats fetch — deliberately excludes payment_status/ordering/page so
  // paging, sorting, or picking a tile never refetches the tiles themselves.
  const statsParams = useMemo(
    () => ({ search: filterParams.search, type: filterParams.type, phase: filterParams.phase, year: filterParams.year }),
    [filterParams.search, filterParams.type, filterParams.phase, filterParams.year]
  );
  useEffect(() => {
    dispatch(onGetContractStats(statsParams)).catch((err) =>
      console.error("Failed to fetch contract stats:", err)
    );
  }, [dispatch, statsParams]);

  const updateFilter = useCallback((key, value) => {
    setFilterParams((prev) => {
      const next = { ...prev };
      if (value) next[key] = value;
      else delete next[key];
      return paramsEqual(prev, next) ? prev : next;
    });
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearchInput("");
    setFilterParams({ ordering: DEFAULT_ORDERING });
  }, []);

  const hasAnyFilter = Boolean(
    searchInput || filterParams.type || filterParams.payment_status || filterParams.phase || filterParams.year
  );

  const handleRowClick = useCallback((id) => navigate(`/financial/contracts/${id}`), [navigate]);

  const handleDeleteRequest = useCallback((contract) => {
    contractRef.current = contract;
    setDeleteModal(true);
  }, []);

  const handleDeleteContract = useCallback(async () => {
    const contract = contractRef.current;
    if (!contract || isDeleting) return;
    try {
      setIsDeleting(true);
      await dispatch(onDeleteContract(contract.id));
      setDeleteModal(false);
      dispatch(onGetContracts({ ...filterParams, page, page_size: 20 }));
      dispatch(onGetContractStats(statsParams));
    } catch (err) {
      console.error("Error deleting contract:", err);
    } finally {
      setIsDeleting(false);
    }
  }, [dispatch, isDeleting, filterParams, page, statsParams]);

  const hasContracts = Array.isArray(contracts) && contracts.length > 0;
  const emptyFromFilters = isContractSuccess && !hasContracts && (count === 0 && hasAnyFilter);

  const headline = stats
    ? `${stats.all?.count ?? 0} contracts · ${stats.overdue?.count ?? 0} overdue · ${stats.unscheduled?.count ?? 0} without a due date`
    : "";

  return (
    <div className="page-content">
      <ExportCSVModal show={isExportCSV} onCloseClick={() => setIsExportCSV(false)} data={"null"} />

      <ContractWizardV2
        isOpen={contractModal}
        onClose={() => setContractModal(false)}
        onCreated={() => {
          dispatch(onGetContracts({ ...filterParams, page, page_size: 20 }));
          dispatch(onGetContractStats(statsParams));
        }}
      />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteContract}
        onCloseClick={() => setDeleteModal(false)}
        isDeleting={isDeleting}
      />

      <Container fluid>
        <AppBreadcrumb />

        <div className="d-flex align-items-start flex-wrap gap-3 mb-3">
          <div style={{ flex: "1 1 320px", minWidth: 0 }}>
            <h1 className="mb-0" style={{ fontSize: 27, fontWeight: 650, letterSpacing: "-0.5px" }}>
              Contracts
            </h1>
            <div className="text-muted mt-1" style={{ fontSize: 13.5 }}>{headline}</div>
          </div>
          <div className="d-flex gap-2 align-items-center">
            <button type="button" className="btn btn-outline-secondary" onClick={() => setIsExportCSV(true)}>
              <i className="ri-file-download-line align-bottom me-1"></i> Export CSV
            </button>
            {canDelete && (
              <button type="button" className="btn btn-primary" onClick={() => setContractModal(true)}>
                <i className="ri-add-line align-bottom me-1"></i> New contract
              </button>
            )}
          </div>
        </div>

        <ContractStatTiles
          stats={stats}
          paymentStatus={filterParams.payment_status || ""}
          onSelect={(value) => updateFilter("payment_status", value)}
          formatCurrency={formatCurrency}
        />

        <Card className="mb-3">
          <ContractFilterBar
            searchValue={searchInput}
            onSearchChange={setSearchInput}
            type={filterParams.type || ""}
            onTypeChange={(v) => updateFilter("type", v)}
            paymentStatus={filterParams.payment_status || ""}
            onPaymentStatusChange={(v) => updateFilter("payment_status", v)}
            phase={filterParams.phase || ""}
            onPhaseChange={(v) => updateFilter("phase", v)}
            year={filterParams.year || ""}
            onYearChange={(v) => updateFilter("year", v)}
            phases={phases}
            years={stats?.years}
            hasAnyFilter={hasAnyFilter}
            onClear={handleClearFilters}
          />

          {loading && !hasContracts ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : hasContracts ? (
            <>
              <ContractsTable
                contracts={contracts}
                ordering={filterParams.ordering || DEFAULT_ORDERING}
                onSortChange={(ordering) => updateFilter("ordering", ordering)}
                onRowClick={handleRowClick}
                canDelete={canDelete}
                onDeleteRequest={handleDeleteRequest}
                formatCurrency={formatCurrency}
              />
              <ContractPaginationFooter
                count={count}
                currentPage={currentPage}
                pageSize={pageSize}
                nextPage={nextPage}
                previousPage={previousPage}
                loading={loading}
                onPrev={(p) => p && setPage(p)}
                onNext={(p) => p && setPage(p)}
              />
            </>
          ) : emptyFromFilters ? (
            <div className="text-center py-5">
              <h5 className="text-muted mb-2">No contracts match these filters</h5>
              <p className="text-muted mb-0">Try clearing the payment state or phase filter.</p>
            </div>
          ) : (
            <div className="text-center py-5">
              <h5 className="text-muted mb-2">No contracts yet</h5>
              <p className="text-muted mb-3">Add your first contract to get started.</p>
              {canDelete && (
                <button type="button" className="btn btn-primary" onClick={() => setContractModal(true)}>
                  <i className="ri-add-line align-bottom me-1"></i> New contract
                </button>
              )}
            </div>
          )}
        </Card>
      </Container>
    </div>
  );
};

export default ContractsList;
